import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { PanelLeftClose, PanelLeftOpen, Store } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChatPanel } from '@/components/chat/ChatPanel';
import { CanvasPanel } from '@/components/canvas/CanvasPanel';
import CanvasStepRibbonIndicator from '@/components/canvas/CanvasStepRibbonIndicator';
import { analytics } from '@/services/analytics';
import { useAppSelector } from '@/app/hooks';
import { selectIsAuthenticated } from '@/features/auth/authSlice';
import { selectChatMessages, type Message } from '@/features/chat/chatSlice';
import { clearCanvas } from '@/features/canvas/canvasSlice';
import { clearOrder, selectOrderState, setOrderState } from '@/features/order/orderSlice';
import { store } from '@/app/store';
import { selectSupervisorSnapshot } from '@/features/supervisor/supervisorSlice';
import {
  activateWorkspaceDraft,
  deleteWorkspaceDraft,
  fetchWorkspaceDrafts,
  saveWorkspaceDraft,
  type WorkspaceDraft,
} from '@/services/workspaceDraftService';
import { supervisorService } from '@/services/supervisorService';
import { logging } from '@/services/logging';
import {
  createInitialSupervisorSnapshotPayload,
  rehydrateSupervisorSnapshot,
} from '@/services/supervisorService';
import {
  extractDraftSupervisorSnapshot,
  hasMeaningfulSupervisorSnapshot,
} from '@/services/workspaceDraftSnapshotService';
import { getWorkspaceCanvasMode, WORKSPACE_CANVAS_MODES, WORKSPACE_CANVAS_QUERY_KEY } from '@/components/workspace/workspaceMode';
import { OrderStep } from '@/types/supervisor';

const DRAFT_QUERY_PARAM = 'draftId';
const TEMP_DRAFT_KIND = 'temporary-production-flow';
const MOBILE_CHAT_BREAKPOINT_QUERY = '(max-width: 767.98px)';
const STUDIO_OPEN_CHAT_EVENT = 'studio:open-chat';

type StudioDraftKind = 'studio' | 'etsy' | 'other';

const getWorkspaceDraftKind = (draft: WorkspaceDraft): StudioDraftKind => {
  const metadata = draft.metadata as { kind?: string } | null;

  if (
    draft.source === 'ETSY'
    || metadata?.kind === 'etsy-order'
    || typeof draft.etsyImportedOrderId === 'string'
  ) {
    return 'etsy';
  }

  if (draft.source === 'INTERNAL' || metadata?.kind === 'studio') {
    return 'studio';
  }

  return 'other';
};

const isRestorableStudioDraft = (draft: WorkspaceDraft): boolean => {
  if (!(draft.status === 'ACTIVE' || draft.status === 'PARKED')) {
    return false;
  }

  if (getWorkspaceDraftKind(draft) !== 'studio') {
    return false;
  }

  const metadata = draft.metadata as { kind?: string } | null;
  return metadata?.kind !== TEMP_DRAFT_KIND;
};

const resetStudioWorkspace = (): void => {
  store.dispatch(clearOrder());
  store.dispatch(clearCanvas());
  rehydrateSupervisorSnapshot(createInitialSupervisorSnapshotPayload());
};

type EtsyMatchingArea = 'papers' | 'sizes' | 'addresses' | 'images';

const ASPECT_RATIO_WARNING_THRESHOLD = 0.03;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const getEtsyProductionMissingAreas = (paymentInformation: unknown): EtsyMatchingArea[] => {
  if (!isRecord(paymentInformation) || paymentInformation.source !== 'etsy') {
    return [];
  }

  const summary = paymentInformation.matchingSummary;
  if (!isRecord(summary) || !Array.isArray(summary.missing)) {
    return [];
  }

  return summary.missing.filter((entry): entry is EtsyMatchingArea => (
    entry === 'papers' || entry === 'sizes' || entry === 'addresses' || entry === 'images'
  ));
};

const hasEtsyProductionAspectRatioWarning = (items: ReadonlyArray<unknown>): boolean => {
  if (!Array.isArray(items)) {
    return false;
  }

  return items.some((item) => {
    if (!isRecord(item)) {
      return false;
    }

    const originalWidthPx = item.originalWidthPx;
    const originalHeightPx = item.originalHeightPx;
    const widthIn = item.widthIn;
    const heightIn = item.heightIn;
    if (
      !isPositiveNumber(originalWidthPx)
      || !isPositiveNumber(originalHeightPx)
      || !isPositiveNumber(widthIn)
      || !isPositiveNumber(heightIn)
    ) {
      return false;
    }

    const uploadedRatio = originalWidthPx / originalHeightPx;
    const targetRatio = widthIn / heightIn;
    return Math.abs(uploadedRatio - targetRatio) / targetRatio >= ASPECT_RATIO_WARNING_THRESHOLD;
  });
};

const StudioPage = () => {
  const { t } = useTranslation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const chatMessages = useAppSelector(selectChatMessages);
  const orderState = useAppSelector(selectOrderState);
  const supervisorSnapshot = useAppSelector(selectSupervisorSnapshot);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [activeDraftKind, setActiveDraftKind] = useState<StudioDraftKind>('studio');
  const [isDraftBootstrapComplete, setIsDraftBootstrapComplete] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [isUnreadPreviewOpen, setIsUnreadPreviewOpen] = useState(false);
  const [unreadPreviewMessages, setUnreadPreviewMessages] = useState<Message[]>([]);
  const saveTimeoutRef = useRef<number | null>(null);
  const processedStageRequestRef = useRef<string | null>(null);
  const lastSeenChatMessageCountRef = useRef(0);
  const unreadPreviewRef = useRef<HTMLDivElement | null>(null);

  const requestedDraftId = searchParams.get(DRAFT_QUERY_PARAM);
  const requestedStage = searchParams.get('stage') as OrderStep | null;
  const requestedWorkspaceCanvas = searchParams.get(WORKSPACE_CANVAS_QUERY_KEY);
  const workspaceCanvasMode = getWorkspaceCanvasMode(requestedWorkspaceCanvas);
  const isEtsyWorkspaceRequest = requestedWorkspaceCanvas?.startsWith('etsy-') ?? false;

  const canvasTitle = workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsySetup
    ? t('integrations.etsy.setupTitle', 'Etsy Setup')
    : workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyImport
      ? t('integrations.etsy.import.title', 'Etsy Import')
      : workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyOrders
        ? t('integrations.etsy.openOrdersInbox', 'Open orders inbox')
        : workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyMatching
          ? t('integrations.etsy.matchingTitle', 'Etsy Matching Workspace')
          : workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyToProduction
            ? t('integrations.etsy.toProduction.title', 'Etsy To Production')
      : t('canvas.title');
  const missingEtsyMatchingAreas = getEtsyProductionMissingAreas(orderState.paymentInformation);
  const showEtsyAspectRatioWarning = activeDraftKind === 'etsy' && !workspaceCanvasMode && hasEtsyProductionAspectRatioWarning(orderState.items);
  const showEtsyMissingDataWarning = activeDraftKind === 'etsy' && !workspaceCanvasMode && missingEtsyMatchingAreas.length > 0;
  const showEtsyProductionWarning = showEtsyMissingDataWarning || showEtsyAspectRatioWarning;
  const missingEtsyMatchingLabel = missingEtsyMatchingAreas.map((area) => (
    area === 'papers'
      ? t('integrations.etsy.toProduction.summary.papers', 'papers')
      : area === 'sizes'
        ? t('integrations.etsy.toProduction.summary.sizes', 'sizes')
        : area === 'addresses'
          ? t('integrations.etsy.toProduction.summary.addresses', 'address')
          : t('integrations.etsy.toProduction.summary.images', 'full-size image')
  )).join(', ');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_CHAT_BREAKPOINT_QUERY);
    const updateViewport = () => {
      setIsMobileViewport(mediaQuery.matches);
    };

    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  useEffect(() => {
    if (lastSeenChatMessageCountRef.current === 0) {
      lastSeenChatMessageCountRef.current = chatMessages.length;
      return;
    }

    if (chatMessages.length <= lastSeenChatMessageCountRef.current) {
      lastSeenChatMessageCountRef.current = chatMessages.length;
      return;
    }

    const newMessages = chatMessages.slice(lastSeenChatMessageCountRef.current);
    lastSeenChatMessageCountRef.current = chatMessages.length;

    if (!isChatVisible) {
      const unreadAiMessages = newMessages.filter((message) => message.author === 'ai');
      if (unreadAiMessages.length > 0) {
        setUnreadChatCount((current) => current + unreadAiMessages.length);
        setUnreadPreviewMessages((current) => [...current, ...unreadAiMessages].slice(-2));
      }
    }
  }, [chatMessages, isChatVisible]);

  useEffect(() => {
    if (isChatVisible) {
      setUnreadChatCount(0);
      setIsUnreadPreviewOpen(false);
      setUnreadPreviewMessages([]);
      lastSeenChatMessageCountRef.current = chatMessages.length;
    }
  }, [chatMessages.length, isChatVisible]);

  useEffect(() => {
    if (!isUnreadPreviewOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (unreadPreviewRef.current && !unreadPreviewRef.current.contains(event.target as Node)) {
        setIsUnreadPreviewOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isUnreadPreviewOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleOpenChat = () => {
      setIsChatVisible(true);
      setUnreadChatCount(0);
      setIsUnreadPreviewOpen(false);
      setUnreadPreviewMessages([]);
    };

    window.addEventListener(STUDIO_OPEN_CHAT_EVENT, handleOpenChat);
    return () => window.removeEventListener(STUDIO_OPEN_CHAT_EVENT, handleOpenChat);
  }, []);

  useEffect(() => {
    if (!requestedStage) {
      return;
    }

    if (!isAuthenticated || workspaceCanvasMode || !isDraftBootstrapComplete) {
      return;
    }

    const stageRequestKey = `${requestedDraftId ?? ''}:${requestedStage}`;
    if (processedStageRequestRef.current === stageRequestKey) {
      return;
    }
    processedStageRequestRef.current = stageRequestKey;

    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('stage');
      return next;
    }, { replace: true });

    if (requestedStage !== supervisorSnapshot?.context.activeStep) {
      supervisorService.send({ type: 'UI.GOTO_STEP', step: requestedStage });
    }
  }, [
    isAuthenticated,
    workspaceCanvasMode,
    isDraftBootstrapComplete,
    requestedDraftId,
    requestedStage,
    supervisorSnapshot?.context.activeStep,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      setActiveDraftId(null);
      setActiveDraftKind('studio');
      setIsDraftBootstrapComplete(true);
      return;
    }

    if (workspaceCanvasMode) {
      setActiveDraftId(null);
      setActiveDraftKind('other');
      setIsDraftBootstrapComplete(true);
      return;
    }

    let cancelled = false;

    const bootstrapDraft = async () => {
      try {
        const requestedDraft = requestedDraftId
          ? await activateWorkspaceDraft({ draftId: requestedDraftId })
          : null;

        const resolvedDraft = requestedDraft ?? await (async () => {
          const drafts = await fetchWorkspaceDrafts();
          const preferredDraft = drafts.find(isRestorableStudioDraft);
          if (!preferredDraft) {
            return null;
          }

          return activateWorkspaceDraft({ draftId: preferredDraft.id });
        })();

        if (cancelled) {
          return;
        }

        if (resolvedDraft) {
          setActiveDraftId(resolvedDraft.id);
          setActiveDraftKind(getWorkspaceDraftKind(resolvedDraft));
          const restoredSnapshot = extractDraftSupervisorSnapshot(resolvedDraft.snapshot);
          if (restoredSnapshot) {
            store.dispatch(clearOrder());
            store.dispatch(clearCanvas());
            rehydrateSupervisorSnapshot(restoredSnapshot);
            store.dispatch(
              setOrderState(
                restoredSnapshot.context.order as Parameters<typeof setOrderState>[0],
              ),
            );
          } else {
            resetStudioWorkspace();
          }
        } else {
          setActiveDraftId(null);
          setActiveDraftKind('studio');
          resetStudioWorkspace();
        }
      } catch (error) {
        logging.error('Failed to bootstrap workspace draft in studio', { error, requestedDraftId });
      } finally {
        if (!cancelled) {
          setIsDraftBootstrapComplete(true);
        }
      }
    };

    setIsDraftBootstrapComplete(false);
    void bootstrapDraft();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, requestedDraftId, workspaceCanvasMode]);

  useEffect(() => {
    if (!isAuthenticated || workspaceCanvasMode || !isDraftBootstrapComplete || !supervisorSnapshot) {
      return;
    }

    if (saveTimeoutRef.current != null) {
      window.clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = window.setTimeout(() => {
      void (async () => {
        try {
          const isCompletedStudioDraft =
            activeDraftKind === 'studio' && supervisorSnapshot.context.activeStep === 'done';

          if (isCompletedStudioDraft) {
            if (activeDraftId) {
              await deleteWorkspaceDraft(activeDraftId);
              setActiveDraftId(null);
            }
            setActiveDraftKind('studio');
            return;
          }

          if (!activeDraftId) {
            if (!hasMeaningfulSupervisorSnapshot(supervisorSnapshot)) {
              return;
            }

            const createdDraft = await activateWorkspaceDraft({
              source: 'INTERNAL',
              title: 'Studio draft',
              snapshot: { supervisor: supervisorSnapshot },
              metadata: { kind: 'studio' },
            });
            setActiveDraftId(createdDraft.id);
            setActiveDraftKind('studio');
            return;
          }

          await saveWorkspaceDraft(activeDraftId, {
            snapshot: { supervisor: supervisorSnapshot },
            ...(activeDraftKind === 'studio'
              ? { metadata: { kind: 'studio' } }
              : {}),
          });
        } catch (error) {
          logging.error('Failed to persist workspace draft from studio', {
            error,
            activeDraftId,
          });
        }
      })();
    }, 800);

    return () => {
      if (saveTimeoutRef.current != null) {
        window.clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [activeDraftId, activeDraftKind, isAuthenticated, isDraftBootstrapComplete, supervisorSnapshot, workspaceCanvasMode]);

  if (!isAuthenticated && isEtsyWorkspaceRequest) {
    return (
      <Navigate
        to="/"
        replace
        state={{
          authModal: true,
          authInitialMode: 'login',
          redirectTo: `${location.pathname}${location.search}`,
        }}
      />
    );
  }

  const getMessagePreviewText = (message: Message): string => {
    if (typeof message.content === 'string') {
      return message.content.trim();
    }

    const preview = message.content?.userFacingMessage;
    return typeof preview === 'string' && preview.trim().length > 0
      ? preview.trim()
      : t('chat.previewFallback', 'New message');
  };

  const markUnreadPreviewAsRead = () => {
    setUnreadChatCount(0);
    setIsUnreadPreviewOpen(false);
    setUnreadPreviewMessages([]);
    lastSeenChatMessageCountRef.current = chatMessages.length;
  };

  const openChatPanel = () => {
    setIsChatVisible(true);
    markUnreadPreviewAsRead();
  };

  const workspaceContent = (
    <CardContent className="flex-grow min-h-0 p-0 overflow-y-auto">
      {showEtsyProductionWarning ? (
        <div className="border-b border-amber-200 bg-amber-50/80 px-6 py-3 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
          {showEtsyMissingDataWarning
            ? t('integrations.etsy.toProduction.warningBanner', 'This Etsy order is open in production with missing matching data: {{items}}.', {
                items: missingEtsyMatchingLabel,
              })
            : null}
          {showEtsyMissingDataWarning && showEtsyAspectRatioWarning ? ' ' : null}
          {showEtsyAspectRatioWarning
            ? t('integrations.etsy.toProduction.studioAspectRatioWarning', 'Check print size: uploaded file shape does not match the selected Etsy size.')
            : null}
        </div>
      ) : null}
      {isAuthenticated && !workspaceCanvasMode && !isDraftBootstrapComplete ? null : <CanvasPanel />}
    </CardContent>
  );

  const workspaceCardHeader = (
    <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <CardTitle className="flex items-center gap-2 text-xl font-semibold text-foreground">
        <span>{canvasTitle}</span>
        {activeDraftKind === 'etsy' && !workspaceCanvasMode ? (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 rounded-full border-border/70 bg-muted/30 px-2.5 text-xs font-medium text-muted-foreground hover:bg-muted/50"
            onClick={() => {
              analytics.track('button: ui.studio_back_to_etsy_inbox');
              setSearchParams((current) => {
                const next = new URLSearchParams(current);
                next.set(WORKSPACE_CANVAS_QUERY_KEY, WORKSPACE_CANVAS_MODES.etsyOrders);
                next.delete(DRAFT_QUERY_PARAM);
                return next;
              });
            }}
          >
            <Store className="h-3.5 w-3.5" />
            {t('integrations.etsy.backToInbox', 'Back to Etsy Inbox')}
          </Button>
        ) : null}
      </CardTitle>
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        {isAuthenticated && !workspaceCanvasMode && isDraftBootstrapComplete ? <CanvasStepRibbonIndicator /> : null}
      </div>
    </CardHeader>
  );

  const chatCard = (
    <div className="relative h-full">
      <Card className="flex h-full min-h-0 flex-col">
        <CardHeader>
          <CardTitle>{t('chat.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex-grow min-h-0 p-0 overflow-y-auto">
          <ChatPanel />
        </CardContent>
      </Card>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="absolute right-0 top-1/2 z-20 h-14 w-10 -translate-y-1/2 translate-x-1/4 rounded-xl border-border bg-background shadow-sm"
        onClick={() => {
          setIsChatVisible(false);
          setUnreadChatCount(0);
          setIsUnreadPreviewOpen(false);
          setUnreadPreviewMessages([]);
        }}
        aria-label={t('chat.hidePanel', 'Hide chat')}
        title={t('chat.hidePanel', 'Hide chat')}
      >
        <PanelLeftClose className="h-5 w-5" />
      </Button>
    </div>
  );

  const closedWorkspaceCard = (
    <Card className="relative flex h-full min-h-0 flex-col">
      {isUnreadPreviewOpen && unreadPreviewMessages.length > 0 ? (
        <div
          ref={unreadPreviewRef}
          className="absolute left-14 top-1/2 z-30 flex w-[min(18rem,calc(100vw-5rem))] -translate-y-1/2 flex-col gap-3 rounded-2xl border border-border bg-background p-3 shadow-lg sm:left-8 sm:w-72"
        >
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t('chat.unreadPreviewTitle', 'New messages')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('chat.unreadPreviewHint', 'Preview the latest replies without opening the full chat.')}
            </p>
          </div>
          <div className="space-y-2">
            {unreadPreviewMessages.map((message) => (
              <div key={message.id} className="rounded-xl bg-muted/60 px-3 py-2 text-sm leading-5 text-foreground">
                {getMessagePreviewText(message)}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {unreadChatCount > unreadPreviewMessages.length
                ? t('chat.unreadPreviewMore', '+{{count}} more', { count: unreadChatCount - unreadPreviewMessages.length })
                : '\u00A0'}
            </span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" className="h-8 px-3" onClick={markUnreadPreviewAsRead}>
                {t('chat.markPreviewRead', 'Read')}
              </Button>
              <Button size="sm" className="h-8" onClick={openChatPanel}>
                {t('chat.openPanel', 'Open chat')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="absolute left-2 top-1/2 z-20 h-14 w-10 -translate-y-1/2 rounded-xl border-border bg-background shadow-sm sm:left-0 sm:-translate-x-1/4"
        onClick={() => {
          if (unreadChatCount > 0) {
            setIsUnreadPreviewOpen((current) => !current);
            return;
          }

          openChatPanel();
        }}
        aria-label={unreadChatCount > 0 ? t('chat.previewUnread', 'Preview unread messages') : t('chat.showPanel', 'Show chat')}
        title={unreadChatCount > 0 ? t('chat.previewUnread', 'Preview unread messages') : t('chat.showPanel', 'Show chat')}
      >
        <PanelLeftOpen className="h-5 w-5" />
        {unreadChatCount > 0 ? (
          <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-green px-1 text-[11px] font-semibold leading-none text-white">
            {unreadChatCount > 9 ? '9+' : unreadChatCount}
          </span>
        ) : null}
      </Button>
      {workspaceCardHeader}
      {workspaceContent}
    </Card>
  );

  return (
    <div className="h-[calc(100vh-theme(spacing.20))] flex flex-col p-4 md:p-6">
      {isMobileViewport ? (
        isChatVisible ? chatCard : closedWorkspaceCard
      ) : isChatVisible ? (
        <ResizablePanelGroup direction="horizontal" className="flex-grow h-0 min-h-0 rounded-lg border">
          <ResizablePanel defaultSize={30} minSize={20}>
            {chatCard}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={70} minSize={40}>
            <Card className="h-full flex flex-col">
              {workspaceCardHeader}
              {workspaceContent}
            </Card>
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        closedWorkspaceCard
      )}
    </div>
  );
};

export default StudioPage;
